// Resizes a picked photo in the browser before upload: phone camera / gallery photos are often 5-12 MB,
// a vehicle photo needs ~1600px. Falls back to the original file if anything fails or it would get bigger.

const decode = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      // imageOrientation keeps phone photos upright (EXIF rotation)
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close && bitmap.close() };
    } catch (e) {
      // fall through to <img>
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, release: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
};

export const compressImage = async (file, { maxSide = 1600, quality = 0.8 } = {}) => {
  let decoded = null;
  try {
    decoded = await decode(file);
    const scale = Math.min(1, maxSide / Math.max(decoded.width, decoded.height));
    const width = Math.max(1, Math.round(decoded.width * scale));
    const height = Math.max(1, Math.round(decoded.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // White background so transparent PNGs don't turn black as JPEG
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(decoded.source, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;

    const baseName = (file.name || "photo").replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], baseName + ".jpg", { type: "image/jpeg", lastModified: Date.now() });
  } catch (e) {
    console.error("[VehiclePhotos] Unable to resize photo, uploading the original", e);
    return file;
  } finally {
    if (decoded) decoded.release();
  }
};
